import React, { useEffect, useRef, useState } from "react";
import Chip, { ChipTone } from "../Chip";
import recordClasses from "./record.module.scss";
import classes from "./statusMenu.module.scss";

/** One value the pill can be switched to. */
export interface StatusMenuOption {
    value: string;
    label: string;
    /** Pill and dot colour from the --status-* tokens. Neutral when omitted. */
    tone?: ChipTone;
    /** Hover text — what this value means. */
    title?: string;
}

interface StatusMenuProps {
    /** The saved value. Controlled: the pill shows a pick only until onChange settles. */
    value: string;
    /** Pill text — defaults to the matching option's label. */
    label?: React.ReactNode;
    /** Pill colour — defaults to the matching option's tone. */
    tone?: ChipTone;
    options: StatusMenuOption[];
    /**
     * Saves the pick straight away. While the returned promise is pending the
     * pill shows a spinner and blocks clicks; on reject it falls back to
     * `value`. The caller toasts the error.
     */
    onChange: (value: string) => Promise<unknown> | void;
    /** What the pill changes — "status", "severity". The tooltip reads "Change status: New". */
    title?: string;
    /** No permission to change it: a plain pill, no caret, no button. */
    disabled?: boolean;
}

const capitalise = (text: string): string => (text ? text[0].toUpperCase() + text.slice(1) : text);

/**
 * A status pill that is also the control to change it (docs/ui-design-rules
 * §2B: status changes sit in the header). Looks like Chip; opens the same
 * menu as the record header's "More"; a pick saves immediately.
 */
const StatusMenu: React.FC<StatusMenuProps> = ({ value, label, tone, options, onChange, title = "status", disabled }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [pendingValue, setPendingValue] = useState<string | null>(null);
    const wrapperRef = useRef<HTMLDivElement>(null);
    const triggerRef = useRef<HTMLButtonElement>(null);
    const menuRef = useRef<HTMLDivElement>(null);

    const shownValue = pendingValue ?? value;
    const shownOption = options.find((option) => option.value === shownValue);
    const shownLabel = pendingValue !== null ? (shownOption?.label ?? pendingValue) : (label ?? shownOption?.label ?? value);
    const shownTone = pendingValue !== null ? (shownOption?.tone ?? "neutral") : (tone ?? shownOption?.tone ?? "neutral");
    const plainLabel = typeof shownLabel === "string" ? shownLabel : (shownOption?.label ?? shownValue);
    const isPending = pendingValue !== null;

    useEffect(() => {
        if (!isOpen) return;
        const handleOutside = (event: MouseEvent) => {
            if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) setIsOpen(false);
        };
        document.addEventListener("mousedown", handleOutside);
        // Focus the first item the user can pick, so arrows and Enter work straight away.
        const first = menuRef.current?.querySelector<HTMLButtonElement>("button:not(:disabled)");
        first?.focus();
        return () => document.removeEventListener("mousedown", handleOutside);
    }, [isOpen]);

    if (disabled) {
        return <Chip tone={shownTone} title={`${capitalise(title)}: ${plainLabel}`}>{shownLabel}</Chip>;
    }

    const close = (refocus: boolean) => {
        setIsOpen(false);
        if (refocus) triggerRef.current?.focus();
    };

    const handlePick = async (next: string) => {
        close(true);
        if (next === value) return;
        setPendingValue(next);
        try {
            await onChange(next);
        } catch {
            // Falls back to `value`; the caller says what went wrong.
        } finally {
            setPendingValue(null);
        }
    };

    const handleMenuKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
        const items = Array.from(menuRef.current?.querySelectorAll<HTMLButtonElement>("button:not(:disabled)") ?? []);
        const index = items.indexOf(document.activeElement as HTMLButtonElement);
        if (event.key === "Escape") {
            event.preventDefault();
            close(true);
        } else if (event.key === "Tab") {
            close(false);
        } else if (event.key === "ArrowDown") {
            event.preventDefault();
            items[(index + 1) % items.length]?.focus();
        } else if (event.key === "ArrowUp") {
            event.preventDefault();
            items[(index - 1 + items.length) % items.length]?.focus();
        } else if (event.key === "Home") {
            event.preventDefault();
            items[0]?.focus();
        } else if (event.key === "End") {
            event.preventDefault();
            items[items.length - 1]?.focus();
        }
    };

    return (
        <div ref={wrapperRef} className={classes.wrapper}>
            <button
                ref={triggerRef}
                type="button"
                className={`btn-unstyled crx-chip crx-chip--${shownTone} ${classes.trigger} ${classes[`tone_${shownTone}`]}`}
                title={`Change ${title}: ${plainLabel}`}
                aria-haspopup="menu"
                aria-expanded={isOpen}
                aria-busy={isPending || undefined}
                disabled={isPending}
                onClick={() => setIsOpen((previous) => !previous)}
                onKeyDown={(event) => {
                    if (event.key === "ArrowDown" && !isOpen) {
                        event.preventDefault();
                        setIsOpen(true);
                    }
                }}
            >
                <span className="crx-chip-label">{shownLabel}</span>
                {isPending
                    ? <span className={`spinner-border ${classes.spinner}`} aria-hidden="true" />
                    : <i className={`bi bi-chevron-down ${classes.caret}`} aria-hidden="true" />}
            </button>
            {isOpen && (
                <div
                    ref={menuRef}
                    className={`${recordClasses.menu} ${classes.menu}`}
                    role="menu"
                    aria-label={`Change ${title}`}
                    onKeyDown={handleMenuKeyDown}
                >
                    {options.map((option) => {
                        const isCurrent = option.value === value;
                        return (
                            <button
                                key={option.value}
                                type="button"
                                role="menuitemradio"
                                aria-checked={isCurrent}
                                className={`btn-unstyled ${recordClasses.menuItem} ${classes.item}`}
                                title={option.title ?? (isCurrent ? `Current ${title}` : `Set ${title} to ${option.label}`)}
                                disabled={isCurrent}
                                onClick={() => handlePick(option.value)}
                            >
                                <span className={`${classes.dot} ${classes[`dot_${option.tone ?? "neutral"}`]}`} aria-hidden="true" />
                                <span>{option.label}</span>
                                {isCurrent && <i className={`bi bi-check2 ${classes.check}`} aria-hidden="true" />}
                            </button>
                        );
                    })}
                </div>
            )}
        </div>
    );
};

export default StatusMenu;
